import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Plus } from 'lucide-react';

interface AddVaccinationModalProps {
  onClose: () => void;
  initialVaccine?: string;
  onAdd: (vaccination: {
    vaccine: string;
    dateAdministered: string;
    expiryDate: string;
    boosterDate?: string;
    administeredBy: string;
    clinicName: string;
    batchNumber: string;
    notes?: string;
  }) => void;
}

const commonVaccines = [
  // Core Dog Vaccines
  {
    value: 'Rabies',
    label: 'Rabies (Dogs & Cats)',
    labelIt: 'Rabbia (Cani e Gatti)',
    category: 'Core',
    categoryIt: 'Base',
  },
  {
    value: 'DHPP',
    label: 'DHPP - Distemper, Hepatitis, Parainfluenza, Parvovirus (Dogs)',
    labelIt: 'DHPP - Cimurro, Epatite, Parainfluenza, Parvovirosi (Cani)',
    category: 'Core - Dogs',
    categoryIt: 'Base - Cani',
  },
  {
    value: 'Distemper',
    label: 'Canine Distemper (Dogs)',
    labelIt: 'Cimurro canino (Cani)',
    category: 'Core - Dogs',
    categoryIt: 'Base - Cani',
  },
  {
    value: 'Parvovirus',
    label: 'Parvovirus (Dogs)',
    labelIt: 'Parvovirosi (Cani)',
    category: 'Core - Dogs',
    categoryIt: 'Base - Cani',
  },
  {
    value: 'Adenovirus',
    label: 'Adenovirus/Hepatitis (Dogs)',
    category: 'Core - Dogs',
    labelIt: 'Adenovirus/Epatite (Cani)',
    categoryIt: 'Base - Cani',
  },

  // Non-Core Dog Vaccines
  {
    value: 'Bordetella',
    label: 'Bordetella (Kennel Cough) (Dogs)',
    category: 'Non-Core - Dogs',
    labelIt: 'Bordetella (Tosse dei canili) (Cani)',
    categoryIt: 'Non base - Cani',
  },
  {
    value: 'Leptospirosis',
    label: 'Leptospirosis (Dogs)',
    category: 'Non-Core - Dogs',
    labelIt: 'Leptospirosi (Cani)',
    categoryIt: 'Non base - Cani',
  },
  {
    value: 'Lyme Disease',
    label: 'Lyme Disease (Dogs)',
    category: 'Non-Core - Dogs',
    labelIt: 'Malattia di Lyme (Cani)',
    categoryIt: 'Non base - Cani',
  },
  {
    value: 'Canine Influenza H3N8',
    label: 'Canine Influenza H3N8 (Dogs)',
    category: 'Non-Core - Dogs',
    labelIt: 'Influenza canina H3N8 (Cani)',
    categoryIt: 'Non base - Cani',
  },
  {
    value: 'Canine Influenza H3N2',
    label: 'Canine Influenza H3N2 (Dogs)',
    category: 'Non-Core - Dogs',
    labelIt: 'Influenza canina H3N2 (Cani)',
    categoryIt: 'Non base - Cani',
  },
  {
    value: 'Parainfluenza',
    label: 'Parainfluenza (Dogs)',
    category: 'Non-Core - Dogs',
    labelIt: 'Parainfluenza (Cani)',
    categoryIt: 'Non base - Cani',
  },
  {
    value: 'Coronavirus',
    label: 'Coronavirus (Dogs)',
    category: 'Non-Core - Dogs',
    labelIt: 'Coronavirus (Cani)',
    categoryIt: 'Non base - Cani',
  },

  // Core Cat Vaccines
  {
    value: 'FVRCP',
    label:
      'FVRCP - Feline Viral Rhinotracheitis, Calicivirus, Panleukopenia (Cats)',
    category: 'Core - Cats',
    labelIt:
      'FVRCP - Rinotracheite virale felina, Calicivirus, Panleucopenia (Gatti)',
    categoryIt: 'Base - Gatti',
  },
  {
    value: 'Feline Panleukopenia',
    label: 'Feline Panleukopenia (Cats)',
    category: 'Core - Cats',
    labelIt: 'Panleucopenia felina (Gatti)',
    categoryIt: 'Base - Gatti',
  },
  {
    value: 'Feline Herpesvirus',
    label: 'Feline Herpesvirus (Cats)',
    category: 'Core - Cats',
    labelIt: 'Herpesvirus felino (Gatti)',
    categoryIt: 'Base - Gatti',
  },
  {
    value: 'Feline Calicivirus',
    label: 'Feline Calicivirus (Cats)',
    category: 'Core - Cats',
    labelIt: 'Calicivirus felino (Gatti)',
    categoryIt: 'Base - Gatti',
  },

  // Non-Core Cat Vaccines
  {
    value: 'FeLV',
    label: 'FeLV - Feline Leukemia Virus (Cats)',
    category: 'Non-Core - Cats',
    labelIt: 'FeLV - Virus della leucemia felina (Gatti)',
    categoryIt: 'Non base - Gatti',
  },
  {
    value: 'FIV',
    label: 'FIV - Feline Immunodeficiency Virus (Cats)',
    category: 'Non-Core - Cats',
    labelIt: "FIV - Virus dell'immunodeficienza felina (Gatti)",
    categoryIt: 'Non base - Gatti',
  },
  {
    value: 'Chlamydia',
    label: 'Chlamydia (Cats)',
    category: 'Non-Core - Cats',
    labelIt: 'Clamidia (Gatti)',
    categoryIt: 'Non base - Gatti',
  },
  {
    value: 'Feline Bordetella',
    label: 'Feline Bordetella (Cats)',
    category: 'Non-Core - Cats',
    labelIt: 'Bordetella felina (Gatti)',
    categoryIt: 'Non base - Gatti',
  },

  // Other
  {
    value: 'Other',
    label: '🔧 Other (Manual Input)',
    labelIt: '🔧 Altro (Inserimento manuale)',
    category: 'Other',
    categoryIt: 'Altro',
  },
];

export function AddVaccinationModal({
  onClose,
  onAdd,
  initialVaccine = '',
}: AddVaccinationModalProps) {
  const { t, i18n } = useTranslation();
  const isItalian = (i18n.resolvedLanguage || i18n.language || '')
    .toLowerCase()
    .startsWith('it');
  const [formData, setFormData] = useState({
    vaccine: initialVaccine,
    customVaccine: '',
    dateAdministered: '',
    expiryDate: '',
    boosterDate: '',
    administeredBy: '',
    clinicName: '',
    batchNumber: '',
    notes: '',
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const finalVaccineName =
      formData.vaccine === 'Other' ? formData.customVaccine : formData.vaccine;

    onAdd({
      vaccine: finalVaccineName,
      dateAdministered: formData.dateAdministered,
      expiryDate: formData.expiryDate,
      boosterDate: formData.boosterDate || undefined,
      administeredBy: formData.administeredBy,
      clinicName: formData.clinicName,
      batchNumber: formData.batchNumber,
      notes: formData.notes || undefined,
    });
    onClose();
  };

  const sanitizeKey = (s: string) =>
    s
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_|_$/g, '');

  // Group vaccines by stable English category
  const vaccinesByCategory = commonVaccines.reduce(
    (acc, vaccine) => {
      if (!acc[vaccine.category]) {
        acc[vaccine.category] = [];
      }
      acc[vaccine.category].push(vaccine);
      return acc;
    },
    {} as Record<string, typeof commonVaccines>
  );

  return (
    <div className="fixed inset-0 bg-black/50 dark:bg-black/70 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-[#16151D] rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col border dark:border-white/8">
        <div className="bg-gradient-to-r from-blue-600 to-purple-600 dark:from-blue-700 dark:to-purple-700 text-white p-6 flex-shrink-0">
          <div className="flex justify-between items-center">
            <div>
              <h2
                className="text-2xl mb-1"
                style={{ fontFamily: 'Sora, sans-serif', fontWeight: 700 }}
              >
                {t('features.healthRecords.addVaccinationModal.title')}
              </h2>
              <p
                className="text-sm opacity-90"
                style={{ fontFamily: 'Inter, sans-serif' }}
              >
                {t('features.healthRecords.addVaccinationModal.subtitle')}
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-white/80 hover:text-white"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        <form
          onSubmit={handleSubmit}
          className="flex-1 min-h-0 overflow-auto p-6 bg-white dark:bg-[#16151D]"
        >
          <div className="space-y-4">
            {/* Vaccine Name Dropdown */}
            <div>
              <label
                className="block text-sm text-gray-700 dark:text-white/90 mb-2"
                style={{ fontFamily: 'Inter, sans-serif' }}
              >
                {t('features.healthRecords.addVaccinationModal.vaccineName')}{' '}
                <span className="text-red-500 dark:text-red-400">*</span>
              </label>
              <select
                required
                value={formData.vaccine}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    vaccine: e.target.value,
                    customVaccine: '',
                  })
                }
                className="w-full px-4 py-2 border border-gray-300 dark:border-white/20 rounded-lg focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-transparent bg-white dark:bg-[#0F0E16] text-gray-900 dark:text-white"
                style={{ fontFamily: 'Inter, sans-serif' }}
              >
                <option value="">
                  {t(
                    'features.healthRecords.addVaccinationModal.selectVaccine'
                  )}
                </option>
                {Object.entries(vaccinesByCategory).map(
                  ([category, vaccines]) => {
                    const categoryLabel = isItalian
                      ? vaccines[0].categoryIt
                      : t(
                          `features.healthRecords.addVaccinationModal.vaccineCategories.${sanitizeKey(category)}`,
                          category
                        );
                    return (
                      <optgroup key={category} label={categoryLabel}>
                        {vaccines.map((vaccine) => (
                          <option key={vaccine.value} value={vaccine.value}>
                            {isItalian
                              ? vaccine.labelIt
                              : t(
                                  `features.healthRecords.addVaccinationModal.vaccines.${sanitizeKey(vaccine.value)}`,
                                  vaccine.label
                                )}
                          </option>
                        ))}
                      </optgroup>
                    );
                  }
                )}
              </select>
            </div>

            {/* Custom Vaccine Name (shown when "Other" is selected) */}
            {formData.vaccine === 'Other' && (
              <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-500/30 rounded-lg p-4">
                <label
                  className="block text-sm text-gray-700 dark:text-white/90 mb-2"
                  style={{ fontFamily: 'Inter, sans-serif' }}
                >
                  {t(
                    'features.healthRecords.addVaccinationModal.customVaccineName'
                  )}{' '}
                  <span className="text-red-500 dark:text-red-400">*</span>
                </label>
                <input
                  type="text"
                  required
                  value={formData.customVaccine}
                  onChange={(e) =>
                    setFormData({ ...formData, customVaccine: e.target.value })
                  }
                  className="w-full px-4 py-2 border border-gray-300 dark:border-white/20 rounded-lg focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-transparent bg-white dark:bg-[#0F0E16] text-gray-900 dark:text-white placeholder:text-gray-400 dark:placeholder:text-white/40"
                  style={{ fontFamily: 'Inter, sans-serif' }}
                  placeholder={t(
                    'features.healthRecords.addVaccinationModal.customVaccineName'
                  )}
                />
                <p
                  className="text-xs text-yellow-700 dark:text-yellow-300 mt-2"
                  style={{ fontFamily: 'Inter, sans-serif' }}
                >
                  💡{' '}
                  {t(
                    'features.healthRecords.addVaccinationModal.customVaccineHint'
                  )}
                </p>
              </div>
            )}

            {/* Date Administered */}
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label
                  className="block text-sm text-gray-700 dark:text-white/90 mb-2"
                  style={{ fontFamily: 'Inter, sans-serif' }}
                >
                  {t(
                    'features.healthRecords.addVaccinationModal.dateAdministered'
                  )}{' '}
                  <span className="text-red-500 dark:text-red-400">*</span>
                </label>
                <input
                  type="date"
                  required
                  value={formData.dateAdministered}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      dateAdministered: e.target.value,
                    })
                  }
                  className="w-full px-4 py-2 border border-gray-300 dark:border-white/20 rounded-lg focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-transparent bg-white dark:bg-[#0F0E16] text-gray-900 dark:text-white"
                  style={{ fontFamily: 'Inter, sans-serif' }}
                />
              </div>

              <div>
                <label
                  className="block text-sm text-gray-700 dark:text-white/90 mb-2"
                  style={{ fontFamily: 'Inter, sans-serif' }}
                >
                  {t('features.healthRecords.addVaccinationModal.expiryDate')}{' '}
                  <span className="text-red-500 dark:text-red-400">*</span>
                </label>
                <input
                  type="date"
                  required
                  value={formData.expiryDate}
                  onChange={(e) =>
                    setFormData({ ...formData, expiryDate: e.target.value })
                  }
                  className="w-full px-4 py-2 border border-gray-300 dark:border-white/20 rounded-lg focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-transparent bg-white dark:bg-[#0F0E16] text-gray-900 dark:text-white"
                  style={{ fontFamily: 'Inter, sans-serif' }}
                />
              </div>
            </div>

            {/* Booster Date */}
            <div>
              <label
                className="block text-sm text-gray-700 dark:text-white/90 mb-2"
                style={{ fontFamily: 'Inter, sans-serif' }}
              >
                {t(
                  'features.healthRecords.addVaccinationModal.boosterDueOptional'
                )}
              </label>
              <input
                type="date"
                value={formData.boosterDate}
                onChange={(e) =>
                  setFormData({ ...formData, boosterDate: e.target.value })
                }
                className="w-full px-4 py-2 border border-gray-300 dark:border-white/20 rounded-lg focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-transparent bg-white dark:bg-[#0F0E16] text-gray-900 dark:text-white"
                style={{ fontFamily: 'Inter, sans-serif' }}
              />
            </div>

            {/* Administered By */}
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label
                  className="block text-sm text-gray-700 dark:text-white/90 mb-2"
                  style={{ fontFamily: 'Inter, sans-serif' }}
                >
                  {t(
                    'features.healthRecords.addVaccinationModal.administeredBy'
                  )}{' '}
                  <span className="text-red-500 dark:text-red-400">*</span>
                </label>
                <input
                  type="text"
                  required
                  value={formData.administeredBy}
                  onChange={(e) =>
                    setFormData({ ...formData, administeredBy: e.target.value })
                  }
                  className="w-full px-4 py-2 border border-gray-300 dark:border-white/20 rounded-lg focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-transparent bg-white dark:bg-[#0F0E16] text-gray-900 dark:text-white placeholder:text-gray-400 dark:placeholder:text-white/40"
                  style={{ fontFamily: 'Inter, sans-serif' }}
                  placeholder={t(
                    'features.healthRecords.addVaccinationModal.administeredByPlaceholder'
                  )}
                />
              </div>

              <div>
                <label
                  className="block text-sm text-gray-700 dark:text-white/90 mb-2"
                  style={{ fontFamily: 'Inter, sans-serif' }}
                >
                  {t('features.healthRecords.addVaccinationModal.clinicName')}{' '}
                  <span className="text-red-500 dark:text-red-400">*</span>
                </label>
                <input
                  type="text"
                  required
                  value={formData.clinicName}
                  onChange={(e) =>
                    setFormData({ ...formData, clinicName: e.target.value })
                  }
                  className="w-full px-4 py-2 border border-gray-300 dark:border-white/20 rounded-lg focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-transparent bg-white dark:bg-[#0F0E16] text-gray-900 dark:text-white placeholder:text-gray-400 dark:placeholder:text-white/40"
                  style={{ fontFamily: 'Inter, sans-serif' }}
                  placeholder={t(
                    'features.healthRecords.addVaccinationModal.clinicNamePlaceholder'
                  )}
                />
              </div>
            </div>

            {/* Batch Number */}
            <div>
              <label
                className="block text-sm text-gray-700 dark:text-white/90 mb-2"
                style={{ fontFamily: 'Inter, sans-serif' }}
              >
                {t('features.healthRecords.addVaccinationModal.batchNumber')}{' '}
                <span className="text-red-500 dark:text-red-400">*</span>
              </label>
              <input
                type="text"
                required
                value={formData.batchNumber}
                onChange={(e) =>
                  setFormData({ ...formData, batchNumber: e.target.value })
                }
                className="w-full px-4 py-2 border border-gray-300 dark:border-white/20 rounded-lg focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-transparent bg-white dark:bg-[#0F0E16] text-gray-900 dark:text-white placeholder:text-gray-400 dark:placeholder:text-white/40"
                style={{ fontFamily: 'Inter, sans-serif' }}
                placeholder={t(
                  'features.healthRecords.addVaccinationModal.batchPlaceholder'
                )}
              />
            </div>

            {/* Notes */}
            <div>
              <label
                className="block text-sm text-gray-700 dark:text-white/90 mb-2"
                style={{ fontFamily: 'Inter, sans-serif' }}
              >
                {t('features.healthRecords.addVaccinationModal.notesOptional')}
              </label>
              <textarea
                value={formData.notes}
                onChange={(e) =>
                  setFormData({ ...formData, notes: e.target.value })
                }
                rows={3}
                className="w-full px-4 py-2 border border-gray-300 dark:border-white/20 rounded-lg focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-transparent bg-white dark:bg-[#0F0E16] text-gray-900 dark:text-white placeholder:text-gray-400 dark:placeholder:text-white/40"
                style={{ fontFamily: 'Inter, sans-serif' }}
                placeholder={t(
                  'features.healthRecords.addVaccinationModal.notesPlaceholder'
                )}
              />
            </div>

            {/* Info Notice */}
            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-500/30 rounded-lg p-4">
              <p
                className="text-sm text-blue-800 dark:text-blue-200"
                style={{ fontFamily: 'Inter, sans-serif' }}
              >
                💡{' '}
                {t(
                  'features.healthRecords.addVaccinationModal.blockchainNotice'
                )}
              </p>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3 mt-6 pt-6 border-t border-gray-200 dark:border-white/8">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-6 py-3 border border-gray-300 dark:border-white/20 rounded-lg hover:bg-gray-50 dark:hover:bg-white/5 transition-colors text-gray-900 dark:text-white"
              style={{ fontFamily: 'Sora, sans-serif', fontWeight: 600 }}
            >
              {t('features.healthRecords.addVaccinationModal.cancel')}
            </button>
            <button
              type="submit"
              className="flex-1 px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 dark:from-blue-700 dark:to-purple-700 text-white rounded-lg hover:opacity-90 transition-opacity flex items-center justify-center gap-2"
              style={{ fontFamily: 'Sora, sans-serif', fontWeight: 600 }}
            >
              <Plus className="w-5 h-5" />
              {t('features.healthRecords.addVaccinationModal.addVaccination')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
